/**
 * manifestoVoice.js
 *
 * Standalone player for the hero manifesto voice. Historically the voice was owned by the legacy
 * Three.js Sketch (ManifestoSound, src/containers/Home/Sketch/modules/Audio.js). In Shimmering Prism
 * mode that Sketch is never instantiated (SketchContext.sketch is null), so Home routes manifesto
 * audio here instead — a minimal HTMLAudioElement singleton, the same pattern as the live
 * BackgroundAudioProvider.
 *
 * Plays the same asset (visuals1.mp3), once per activation (no loop). It plays INDEPENDENTLY of the
 * global AudioToggle mute — a deliberate 2s hold is a clear intent to activate the ritual.
 *
 * A Web Audio AnalyserNode meters the playback so the background can react to the voice volume
 * (getManifestoVoiceLevel). ShimmeringPrism reads that level each frame to modulate shininess.
 */

import manifesto from '../assets/Visuals/visuals1.mp3';

const MANIFESTO_VOLUME = 1.0; // full volume — spoken manifesto (tunable)
const LEVEL_RELEASE = 0.9;    // per-read decay — fast attack, slow release (no harsh flicker)

let audio = null;
let ctx = null;
let analyser = null;
let timeData = null;
let level = 0; // smoothed RMS amplitude (0..~1)

function ensureAudio() {
    if (audio) return audio;
    audio = new Audio(manifesto);
    audio.preload = 'auto';
    audio.volume = MANIFESTO_VOLUME;
    // Web Audio metering, set up once (within the play gesture). NOTE: createMediaElementSource may be
    // called only once per element, and once the element is routed through the graph its output only
    // reaches the speakers via analyser → destination — so that connection is required. If any of this
    // throws (no Web Audio / blocked), leave analyser null: the element still plays via its default
    // output and getManifestoVoiceLevel() returns 0.
    try {
        const AC = window.AudioContext || window.webkitAudioContext || window.mozAudioContext;
        if (AC) {
            ctx = new AC();
            const source = ctx.createMediaElementSource(audio);
            analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            analyser.connect(ctx.destination);
            timeData = new Uint8Array(analyser.fftSize);
        }
    } catch (e) {
        ctx = null;
        analyser = null;
        timeData = null;
    }
    return audio;
}

/** Start the manifesto voice from the top. `onEnded` fires once when it finishes naturally. */
export function playManifestoVoice(onEnded) {
    const a = ensureAudio();
    // Resume the context (user-gesture safe — this is the hold). Without it the metered graph stays
    // suspended and no audio reaches the speakers.
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    a.onended = onEnded || null; // reset hero state when the voice finishes on its own
    a.currentTime = 0;
    const p = a.play(); // user-gesture initiated (the hold) → not subject to autoplay block
    if (p && typeof p.catch === 'function') p.catch(() => {});
}

/** Stop and rewind (on abort / navigation away). */
export function stopManifestoVoice() {
    if (!audio) return;
    audio.onended = null;
    audio.pause();
    audio.currentTime = 0;
    level = 0;
}

/**
 * Current playback position in seconds (0 before first play / if the element isn't created yet). Read once
 * per frame by HomeHero to drive the manifesto subtitles in shimmer mode (where the legacy Sketch — and its
 * getAudioCurrentTime() — doesn't exist).
 */
export function getManifestoVoiceTime() {
    return audio ? audio.currentTime : 0;
}

/**
 * Current voice loudness (0..~1) — smoothed RMS amplitude, fast attack / slow release. Returns 0 when
 * the voice is not playing or metering is unavailable. Read once per frame by the background engine.
 */
export function getManifestoVoiceLevel() {
    if (!analyser || !timeData || !audio || audio.paused) return 0;
    analyser.getByteTimeDomainData(timeData);
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
        const v = (timeData[i] - 128) / 128; // center on 0, normalize to [-1, 1]
        sum += v * v;
    }
    const rms = Math.sqrt(sum / timeData.length);
    level = Math.max(rms, level * LEVEL_RELEASE);
    return level;
}
